import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { QrCode, Download, Archive, FileText, Search, Check } from 'lucide-react';

export default function QRPoster({ selectedTeam }) {
  const [brands, setBrands] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [selectedBgId, setSelectedBgId] = useState('');
  const [outputMode, setOutputMode] = useState('zip');
  const [template, setTemplate] = useState('standard');
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (selectedTeam) {
      loadData();
    }
  }, [selectedTeam]);

  const loadData = async () => {
    try {
      const [b, bg] = await Promise.all([
        axiosInstance.get(`/teams/${selectedTeam}/brands`),
        axiosInstance.get(`/teams/${selectedTeam}/qr-poster/backgrounds`)
      ]);
      setBrands(b.data);
      setBackgrounds(bg.data);
      if (bg.data.length > 0 && !selectedBgId) {
        setSelectedBgId(bg.data[0].id);
      }
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    }
  };

  const toggleBrand = (id) => {
    setSelectedBrandIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const filteredBrands = brands.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  const allFilteredSelected = filteredBrands.length > 0 && filteredBrands.every(b => selectedBrandIds.includes(b.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      // deselect only filtered
      const filteredIds = new Set(filteredBrands.map(b => b.id));
      setSelectedBrandIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const idsToAdd = filteredBrands.map(b => b.id).filter(id => !selectedBrandIds.includes(id));
      setSelectedBrandIds(prev => [...prev, ...idsToAdd]);
    }
  };

  const handleGenerate = async () => {
    if ((template === 'brand_only' || template === 'combined') && selectedBrandIds.length === 0) {
      toast.error('En az bir marka seçin');
      return;
    }
    setGenerating(true);
    try {
      const effectiveOutputMode = template === 'standard' ? 'single_pdf' : outputMode;
      const res = await axiosInstance.post(
        `/teams/${selectedTeam}/qr-poster/generate`,
        {
          brand_ids: (template === 'brand_only' || template === 'combined') ? selectedBrandIds : [],
          background_id: selectedBgId || null,
          output_mode: effectiveOutputMode,
          template: template
        },
        { responseType: 'blob' }
      );
      const isPdf = effectiveOutputMode === 'single_pdf';
      const blob = new Blob([res.data], {
        type: isPdf ? 'application/pdf' : 'application/zip'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isPdf ? 'qr-posters.pdf' : 'qr-posters.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF hazır, indiriliyor');
    } catch (err) {
      // If blob error, try to read
      let msg = 'Oluşturma başarısız';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch (_) {
          /* ignore parse error */
        }
      } else {
        msg = err.response?.data?.detail || msg;
      }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Card className="w-96 text-center overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <QrCode className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">QR poster oluşturmak için önce bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="qr-poster-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">QR Poster Oluştur</h1>
        <p className="text-gray-600">Marka(lar)ı seçin, arkaplanı belirleyin, A4 baskı kalitesinde PDF alın.</p>
      </div>

      {/* Template selector */}
      <Card className="shadow-lg border-0 overflow-hidden mb-6">
        <CardContent className="pt-6">
          <div className="text-sm font-semibold mb-3">Poster Şablonu</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setTemplate('standard')}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${template === 'standard' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
              data-testid="template-standard-button"
            >
              <div className="font-semibold mb-1">Standart Poster</div>
              <p className="text-xs text-gray-600">WhatsApp / Telegram / Instagram / WiFi QR&apos;ları (aktif olanlar).</p>
            </button>
            <button
              type="button"
              onClick={() => setTemplate('brand_only')}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${template === 'brand_only' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
              data-testid="template-brand-only-button"
            >
              <div className="font-semibold mb-1">Sadece Marka Telegram</div>
              <p className="text-xs text-gray-600">Marka adı üstte, Telegram QR&apos;ı altta.</p>
            </button>
            <button
              type="button"
              onClick={() => setTemplate('combined')}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${template === 'combined' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
              data-testid="template-combined-button"
            >
              <div className="font-semibold mb-1">Önlü Arkalı (Birleşik)</div>
              <p className="text-xs text-gray-600">Ön yüz: Standart, Arka yüz: Marka Telegram. Çift taraflı baskı için ideal.</p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Config bar */}
      <Card className="shadow-lg border-0 overflow-hidden mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Background picker */}
            <div>
              <div className="text-sm font-semibold mb-2">Arkaplan</div>
              {backgrounds.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  Henüz arkaplan yok. <a href="/qr-poster-settings" className="text-amber-600 underline">Ayarlar sayfasından</a> ekleyin.
                </p>
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  <button
                    type="button"
                    onClick={() => setSelectedBgId('')}
                    className={`shrink-0 w-20 h-28 rounded-xl border-2 flex items-center justify-center text-xs ${selectedBgId === '' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                    data-testid="bg-none-button"
                  >
                    Arkaplan Yok
                  </button>
                  {backgrounds.map(bg => (
                    <button
                      key={bg.id}
                      type="button"
                      onClick={() => setSelectedBgId(bg.id)}
                      className={`shrink-0 w-20 h-28 rounded-xl border-2 overflow-hidden relative ${selectedBgId === bg.id ? 'border-amber-500' : 'border-gray-200'}`}
                      data-testid={`bg-select-${bg.id}`}
                    >
                      <img src={bg.image_url} alt={bg.name} className="w-full h-full object-cover" />
                      {selectedBgId === bg.id && (
                        <div className="absolute inset-0 bg-amber-500/30 flex items-center justify-center">
                          <Check className="w-6 h-6 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Output mode (brand_only and combined) */}
            {(template === 'brand_only' || template === 'combined') && (
              <div>
                <div className="text-sm font-semibold mb-2">Çıktı Formatı</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setOutputMode('zip')}
                    className={`flex-1 p-3 rounded-xl border-2 flex items-center gap-2 ${outputMode === 'zip' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                    data-testid="output-zip-button"
                  >
                    <Archive className="w-4 h-4" /><span className="text-sm font-medium">ZIP (her marka ayrı PDF)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOutputMode('single_pdf')}
                    className={`flex-1 p-3 rounded-xl border-2 flex items-center gap-2 ${outputMode === 'single_pdf' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'}`}
                    data-testid="output-single-pdf-button"
                  >
                    <FileText className="w-4 h-4" /><span className="text-sm font-medium">Tek PDF (çok sayfa)</span>
                  </button>
                </div>
              </div>
            )}
            {template === 'standard' && (
              <div>
                <div className="text-sm font-semibold mb-2">Çıktı</div>
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Tek A4 PDF (mağaza geneli — marka seçimi gerekmez)
                </div>
              </div>
            )}

            {/* Generate */}
            <div className="flex items-end">
              <Button
                onClick={handleGenerate}
                disabled={generating || ((template === 'brand_only' || template === 'combined') && selectedBrandIds.length === 0)}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white h-12"
                data-testid="generate-poster-button"
              >
                <Download className="w-5 h-5 mr-2" />
                {generating ? 'Oluşturuluyor...' : (template === 'standard' ? 'Poster Oluştur & İndir' : `${selectedBrandIds.length} Poster Oluştur & İndir`)}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brand picker — for brand_only and combined templates */}
      {(template === 'brand_only' || template === 'combined') && (
      <Card className="shadow-lg border-0 overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-900 text-white">
          <div className="flex items-center justify-between">
            <CardTitle>Marka Seçimi ({selectedBrandIds.length}/{brands.length})</CardTitle>
            <Button size="sm" variant="secondary" onClick={toggleSelectAll} data-testid="select-all-button">
              {allFilteredSelected ? 'Seçimi Temizle' : 'Tümünü Seç'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Marka ara..." className="pl-9" data-testid="brand-search-input" />
          </div>
          {filteredBrands.length === 0 ? (
            <p className="text-center py-8 text-gray-500 italic">Marka bulunamadı</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredBrands.map(brand => {
                const selected = selectedBrandIds.includes(brand.id);
                return (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={() => toggleBrand(brand.id)}
                    className={`p-3 rounded-2xl border-2 flex items-center gap-3 text-left transition-all ${selected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                    data-testid={`brand-item-${brand.id}`}
                  >
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${selected ? 'bg-amber-500' : 'bg-gray-100 border border-gray-300'}`}>
                      {selected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{brand.name}</p>
                      {brand.link ? (
                        <p className="text-xs text-gray-500 truncate">{brand.link}</p>
                      ) : (
                        <p className="text-xs text-red-500">Link yok — varsayılan kullanılacak</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
